/**
 * F9: `<input type="datetime-local">` holds a wall-clock time with no zone ("2026-11-01T09:00");
 * the API stores UTC instants. These helpers convert between the two in the browser's time zone
 * (or an explicit IANA zone, for tests and for showing a zone other than the viewer's).
 *
 * DST: a wall time skipped by spring-forward is read as the same wall time after the jump (what
 * browsers do); a wall time repeated by fall-back is read as its first occurrence.
 */

const INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

type WallClock = { year: number; month: number; day: number; hour: number; minute: number };

function parseInput(value: string): WallClock | null {
  const match = INPUT_PATTERN.exec(value.trim());
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  return { year, month, day, hour, minute };
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function formatWallClock(c: WallClock) {
  return `${c.year}-${pad(c.month)}-${pad(c.day)}T${pad(c.hour)}:${pad(c.minute)}`;
}

/** The wall clock an instant shows in `timeZone`. */
function wallClockIn(epochMs: number, timeZone: string): WallClock {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(epochMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
  };
}

/** Offset (ms) of `timeZone` from UTC at an instant. */
function offsetAt(epochMs: number, timeZone: string) {
  const c = wallClockIn(epochMs, timeZone);
  const asUtc = Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute);
  return asUtc - Math.floor(epochMs / 60_000) * 60_000;
}

/** `"2026-11-01T09:00"` (wall time in `timeZone`, default the browser's) → ISO UTC, or null. */
export function localInputToIso(value: string, timeZone?: string): string | null {
  const c = parseInput(value);
  if (!c) return null;

  if (!timeZone) {
    const local = new Date(c.year, c.month - 1, c.day, c.hour, c.minute);
    // 31 April rolls into May: not a real date.
    if (local.getMonth() !== c.month - 1) return null;
    return local.toISOString();
  }

  const guess = Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute);
  if (new Date(guess).getUTCDate() !== c.day) return null;
  const first = guess - offsetAt(guess, timeZone);
  const second = guess - offsetAt(first, timeZone);
  const wanted = formatWallClock(c);
  const exact = [first, second]
    .filter((t) => formatWallClock(wallClockIn(t, timeZone)) === wanted)
    .sort((a, b) => a - b);
  // No candidate shows the wall time: it was skipped by spring-forward — take the later instant.
  const chosen = exact[0] ?? Math.max(first, second);
  return new Date(chosen).toISOString();
}

/** ISO UTC → `"2026-11-01T09:00"` in `timeZone` (default the browser's); "" for none. */
export function isoToLocalInput(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "";
  const epoch = Date.parse(iso);
  if (Number.isNaN(epoch)) return "";
  if (timeZone) return formatWallClock(wallClockIn(epoch, timeZone));
  const d = new Date(epoch);
  return formatWallClock({
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  });
}

/** The viewer's IANA time zone, e.g. "Asia/Kolkata". */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** A date and time in the viewer's zone, for "Locks until …" / "Available from …". */
export function formatLocalDateTime(iso: string, locale?: string): string {
  return new Date(iso).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}
